import React from "react";
import { cn } from "@/lib/utils/cn";

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "primary" | "secondary" | "danger" | "ghost";
  size?: "sm" | "md" | "lg";
  isLoading?: boolean;
}

export default function Button({
  className,
  variant = "primary",
  size = "md",
  isLoading = false,
  children,
  disabled,
  ...props
}: ButtonProps) {
  const variants = {
    primary: "bg-primary-500 text-white focus:ring-primary-500 hover:bg-primary-600 hover:shadow-[0_4px_12px_rgba(0,61,122,0.2)] active:bg-primary-700",
    secondary: "bg-white text-text-secondary border border-border focus:ring-primary-500 hover:bg-surface-primary hover:border-border-dark",
    danger: "bg-status-critical text-white focus:ring-red-500 hover:bg-red-700",
    ghost: "bg-transparent text-text-secondary hover:bg-surface-secondary",
  };

  const sizes = {
    sm: "py-1.5 px-3 text-[12px]",
    md: "py-2.5 px-5 text-[13px]",
    lg: "py-3 px-6 text-sm",
  };

  return (
    <button
      className={cn(
        "inline-flex items-center justify-center font-semibold rounded-button transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed",
        variants[variant],
        sizes[size],
        className
      )}
      disabled={disabled || isLoading}
      {...props}
    >
      {isLoading && (
        <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      )}
      {children}
    </button>
  );
}
